// StudentSignupPre.component: first screen for /signup/student — email or OAuth signup entry points.
// Mistake: invite flow used a school banner; school-id stays on the URL for register without that UI.
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import React from "react";
import Header from "./Header.component";

const signupEmailOpenButtonClass =
    "flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-[#CF3688] py-3.5 pl-3 pr-4 text-base font-semibold text-white shadow-md transition hover:bg-[#A62B6D] focus:outline-none focus:ring-2 focus:ring-[#CF3688]/40";

const SignupEmailEnvelopeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="currentColor" aria-hidden>
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
);

type SignupOauthProviderId = "google" | "line" | "kakao" | "apple";

const OAUTH_BRAND: Record<
    SignupOauthProviderId,
    { logoSrc: string; buttonClass: string; iconWrapClass: string; imgClass: string }
> = {
    google: {
        logoSrc: "/imgs/common/logo/icon-google-logo.svg",
        buttonClass: "bg-[#4285F4] text-white hover:bg-[#3367D6] focus:ring-[#4285F4]/50",
        iconWrapClass: "bg-white",
        imgClass: "h-6 w-6",
    },
    line: {
        logoSrc: "/imgs/common/logo/icon-line-logo.svg",
        buttonClass: "bg-[#06C755] text-white hover:bg-[#05b34c] focus:ring-[#06C755]/50",
        iconWrapClass: "bg-transparent",
        imgClass: "h-7 w-7",
    },
    kakao: {
        logoSrc: "/imgs/common/logo/icon-kakao-logo.svg",
        buttonClass: "bg-[#FEE500] text-[#191919] hover:bg-[#f5dc00] focus:ring-[#FEE500]/60",
        iconWrapClass: "bg-transparent",
        imgClass: "h-6 w-6",
    },
    apple: {
        logoSrc: "/imgs/common/logo/icon-apple-logo-w.svg",
        buttonClass: "bg-black text-white hover:bg-neutral-900 focus:ring-white/30",
        iconWrapClass: "bg-transparent",
        imgClass: "h-6 w-6",
    },
};

const SignupOauthBrandButton = (props: { provider: SignupOauthProviderId; label: string; onClick: () => void }) => {
    const { provider, label, onClick } = props;
    const b = OAUTH_BRAND[provider];
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={`flex w-full items-center gap-3 rounded-xl py-3.5 pl-3 pr-4 text-base font-semibold shadow-md transition focus:outline-none focus:ring-2 ${b.buttonClass}`}
        >
            <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg ${b.iconWrapClass}`}
            >
                <img src={b.logoSrc} alt="" className={b.imgClass} />
            </span>
            <span className="min-w-0 flex-1 text-center">{label}</span>
            <span className="h-10 w-10 shrink-0" aria-hidden />
        </button>
    );
};

const StudentSignupPreComponent = (props: any) => {
    const { onEmailSignup, onGoogleSignup, onLineSignup, onKakaoSignup, onAppleSignup, backUrl } = props;

    return (
        <>
            <Header backUrl={backUrl} />
            <main className="min-h-screen bg-gray-50 px-4 pb-12 pt-10 sm:px-6 lg:px-8">
                <div className="mx-auto w-full max-w-4xl pt-6 sm:pt-6 lg:pt-6">
                    <div className="mx-auto flex w-full max-w-md flex-col items-center">
                        <div className="mb-8 text-center">
                            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.txtPreTitle")}
                            </h1>
                            <p className="mt-3 text-center text-gray-600 text-sm sm:text-base">
                                {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.txtMethodHint")}
                            </p>
                        </div>

                        <div className="flex w-full flex-col gap-3">
                            <button type="button" className={signupEmailOpenButtonClass} onClick={onEmailSignup}>
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center text-white" aria-hidden>
                                    <SignupEmailEnvelopeIcon />
                                </span>
                                <span className="min-w-0 flex-1 text-center">
                                    {getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.btnSignupEmail")}
                                </span>
                                <span className="h-10 w-10 shrink-0" aria-hidden />
                            </button>
                            <SignupOauthBrandButton
                                provider="google"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.btnSignupGoogle")}
                                onClick={onGoogleSignup}
                            />
                            <SignupOauthBrandButton
                                provider="line"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.btnSignupLine")}
                                onClick={onLineSignup}
                            />
                            <SignupOauthBrandButton
                                provider="kakao"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.btnSignupKakao")}
                                onClick={onKakaoSignup}
                            />
                            <SignupOauthBrandButton
                                provider="apple"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.btnSignupApple")}
                                onClick={onAppleSignup}
                            />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default StudentSignupPreComponent;
